import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import api from '../services/api';
import SerpPanel from './SerpPanel';
import QueueHeader, { QUEUE_META } from './QueueHeader';

// ── Volume API call ───────────────────────────────────────────────────────────
async function fetchVolumeData(keywords) {
  try {
    const token = localStorage.getItem('token') || localStorage.getItem('access_token') || '';
    const res = await fetch('/keyword-intelligence/api/v1/keyword-volume/lookup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ keywords, country_code: 'US' }),
    });
    const data = await res.json();
    return data.data || {};
  } catch (e) {
    console.error('Volume fetch error:', e);
    return {};
  }
}

// ── Opportunity Score (0–100) ─────────────────────────────────────────────────
// Combines volume, KD, position, and CPC into a single actionability score.
// Higher = better opportunity to target right now.
function computeOpportunityScore(vol, kd, position, cpc) {
  if (!position) return 0;

  // Position score — highest for pos 4–20 (rankable but not yet #1)
  let posScore = 0;
  if (position >= 1  && position <= 3)  posScore = 60;   // already ranking — maintain
  if (position >= 4  && position <= 10) posScore = 100;  // low-hanging fruit
  if (position >= 11 && position <= 20) posScore = 90;   // quick win potential
  if (position >= 21 && position <= 50) posScore = 50;   // medium effort
  if (position > 50)                    posScore = 20;   // long-term

  // Volume score — log scale so 100 and 100,000 don't dominate equally
  const v = vol || 0;
  let volScore = 0;
  if (v >= 10000) volScore = 100;
  else if (v >= 1000) volScore = 80;
  else if (v >= 100)  volScore = 55;
  else if (v >= 10)   volScore = 30;
  else                volScore = 10;

  // KD score — lower KD = higher score (easier to rank)
  const k = kd !== null && kd !== undefined ? kd : 50;
  const kdScore = Math.max(0, 100 - k);

  // CPC score — higher CPC = more commercial value
  const c = cpc || 0;
  let cpcScore = 0;
  if (c >= 20)     cpcScore = 100;
  else if (c >= 10) cpcScore = 80;
  else if (c >= 5)  cpcScore = 60;
  else if (c >= 1)  cpcScore = 40;
  else              cpcScore = 10;

  // Weighted blend: position matters most, then volume, then KD, then CPC
  const score = (posScore * 0.35) + (volScore * 0.30) + (kdScore * 0.20) + (cpcScore * 0.15);
  return Math.round(score);
}

function oppScoreColor(score) {
  if (score >= 80) return { bg: '#dcfce7', text: '#16a34a', label: 'Excellent' };
  if (score >= 60) return { bg: '#dbeafe', text: '#2563eb', label: 'Good' };
  if (score >= 40) return { bg: '#fef9c3', text: '#ca8a04', label: 'Fair' };
  return                  { bg: '#f1f5f9', text: '#94a3b8', label: 'Low' };
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function formatVolume(v) {
  if (v === null || v === undefined) return null;
  if (v === 0) return null; // treat 0 as low/unknown
  if (v >= 1000000) return (v / 1000000).toFixed(1) + 'M';
  if (v >= 1000)    return (v / 1000).toFixed(1) + 'K';
  return v.toString();
}

function volumeColor(v) {
  if (!v) return '#94a3b8';
  if (v >= 10000) return '#16a34a';
  if (v >= 1000)  return '#2563eb';
  if (v >= 100)   return '#d97706';
  return '#94a3b8';
}

function kdColor(kd) {
  if (kd === null || kd === undefined) return { bg: '#f1f5f9', text: '#94a3b8' };
  if (kd <= 20) return { bg: '#dcfce7', text: '#16a34a' };
  if (kd <= 40) return { bg: '#dbeafe', text: '#2563eb' };
  if (kd <= 60) return { bg: '#fef9c3', text: '#ca8a04' };
  if (kd <= 80) return { bg: '#ffedd5', text: '#ea580c' };
  return             { bg: '#fee2e2', text: '#dc2626' };
}

function kdLabel(kd) {
  if (kd === null || kd === undefined) return '—';
  if (kd <= 20) return 'Easy';
  if (kd <= 40) return 'Low';
  if (kd <= 60) return 'Medium';
  if (kd <= 80) return 'Hard';
  return 'V.Hard';
}

function compColor(c) {
  if (c === 'low')    return { bg: '#dcfce7', text: '#16a34a' };
  if (c === 'medium') return { bg: '#fef9c3', text: '#ca8a04' };
  if (c === 'high')   return { bg: '#fee2e2', text: '#dc2626' };
  return                     { bg: '#f1f5f9', text: '#94a3b8' };
}

function OpportunityBadge({ position, volume }) {
  if (!position || !volume) return null;
  if (position >= 11 && position <= 20 && volume >= 100)
    return <span style={{ marginLeft: 6, fontSize: 10, padding: '2px 6px', borderRadius: 10, background: '#fef9c3', color: '#ca8a04', fontWeight: 700 }}>Quick Win</span>;
  if (position >= 4 && position <= 10 && volume >= 500)
    return <span style={{ marginLeft: 6, fontSize: 10, padding: '2px 6px', borderRadius: 10, background: '#dbeafe', color: '#2563eb', fontWeight: 700 }}>Opportunity</span>;
  return null;
}

// ── Mini sparkline (SVG) ──────────────────────────────────────────────────────
function Sparkline({ data }) {
  if (!data || data.length < 2) return <span style={{ color: '#cbd5e1', fontSize: 11 }}>No data</span>;
  const vals = data.map(d => d.volume || 0);
  const max = Math.max(...vals) || 1;
  const min = Math.min(...vals);
  const W = 80; const H = 28;
  const pts = vals.map((v, i) => {
    const x = (i / (vals.length - 1)) * W;
    const y = H - ((v - min) / (max - min || 1)) * H;
    return `${x},${y}`;
  }).join(' ');
  const first = vals[0]; const last = vals[vals.length - 1];
  const trend = last - first;
  const pct = first > 0 ? Math.round((trend / first) * 100) : 0;
  const color = trend > 0 ? '#16a34a' : trend < 0 ? '#ef4444' : '#94a3b8';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <svg width={W} height={H} style={{ overflow: 'visible' }}>
        <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
      </svg>
      <span style={{ fontSize: 10, color, fontWeight: 600, whiteSpace: 'nowrap' }}>
        {pct > 0 ? '+' : ''}{pct}%
      </span>
    </div>
  );
}

// ── Position trend chart (canvas) ─────────────────────────────────────────────
function TrendChart({ trendData, width = 320, height = 120 }) {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas || !trendData || trendData.length < 2) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width; const H = canvas.height;
    const P = { top: 16, right: 12, bottom: 28, left: 40 };
    const cW = W - P.left - P.right; const cH = H - P.top - P.bottom;
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = '#f8fafc'; ctx.fillRect(0, 0, W, H);
    const positions = trendData.map(d => d.position);
    const maxP = Math.max(...positions) + 2;
    const minP = Math.max(1, Math.min(...positions) - 2);
    const range = maxP - minP || 1;
    const xOf = i => P.left + (i / (trendData.length - 1)) * cW;
    const yOf = v => P.top + ((v - minP) / range) * cH;
    for (let i = 0; i <= 3; i++) {
      const y = P.top + (cH / 3) * i;
      ctx.beginPath(); ctx.strokeStyle = '#e2e8f0'; ctx.lineWidth = 1;
      ctx.moveTo(P.left, y); ctx.lineTo(P.left + cW, y); ctx.stroke();
      const val = Math.round(minP + (range / 3) * (3 - i));
      ctx.fillStyle = '#94a3b8'; ctx.font = '9px system-ui'; ctx.textAlign = 'right';
      ctx.fillText(val, P.left - 4, y + 3);
    }
    const grad = ctx.createLinearGradient(0, P.top, 0, P.top + cH);
    grad.addColorStop(0, 'rgba(102,126,234,0.35)');
    grad.addColorStop(1, 'rgba(102,126,234,0.02)');
    ctx.beginPath();
    trendData.forEach((d, i) => { const x = xOf(i); const y = yOf(d.position); i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y); });
    ctx.lineTo(xOf(trendData.length - 1), P.top + cH); ctx.lineTo(P.left, P.top + cH);
    ctx.closePath(); ctx.fillStyle = grad; ctx.fill();
    ctx.beginPath();
    trendData.forEach((d, i) => { const x = xOf(i); const y = yOf(d.position); i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y); });
    ctx.strokeStyle = '#667eea'; ctx.lineWidth = 2; ctx.stroke();
    trendData.forEach((d, i) => {
      const x = xOf(i); const y = yOf(d.position);
      ctx.beginPath(); ctx.arc(x, y, 3, 0, Math.PI * 2);
      ctx.fillStyle = '#667eea'; ctx.fill();
      ctx.strokeStyle = '#fff'; ctx.lineWidth = 1.5; ctx.stroke();
    });
    ctx.fillStyle = '#94a3b8'; ctx.font = '9px system-ui'; ctx.textAlign = 'center';
    [0, Math.floor(trendData.length / 2), trendData.length - 1].forEach(i => {
      if (trendData[i]) ctx.fillText(trendData[i].date.slice(5), xOf(i), H - 4);
    });
  }, [trendData]);
  return <canvas ref={ref} width={width} height={height} style={{ width: '100%', maxWidth: width, height, borderRadius: 6 }} />;
}


// ── Position Distribution Bar ─────────────────────────────────────────────────
const DIST_BUCKETS = [
  { key: 'top3',   label: 'Top 3',  color: '#10b981', desc: '1–3' },
  { key: 'page1',  label: 'Page 1', color: '#3b82f6', desc: '4–10' },
  { key: 'page2',  label: 'Page 2', color: '#f59e0b', desc: '11–20' },
  { key: 'page3',  label: 'Page 3', color: '#f97316', desc: '21–50' },
  { key: 'beyond', label: 'Beyond', color: '#94a3b8', desc: '51+' },
];

function DistributionBar({ dist, activeBucket, onBucketClick, days }) {
  const total = dist.total || 1;
  const drivingTraffic = (dist.top3?.count || 0) + (dist.page1?.count || 0);
  const drivingPct = Math.round(drivingTraffic / total * 100);

  return (
    <div style={{ marginBottom: 24, padding: '20px 24px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12 }}>
      {/* Hero number */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.6px', fontWeight: 600, marginBottom: 4 }}>
            Driving Traffic
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <span style={{ fontSize: 36, fontWeight: 700, color: '#111827', lineHeight: 1, letterSpacing: '-0.02em' }}>
              {drivingTraffic}
            </span>
            <span style={{ fontSize: 16, color: '#6b7280', fontWeight: 500 }}>
              of {total}
            </span>
            <span style={{ fontSize: 14, color: drivingPct >= 30 ? '#10b981' : drivingPct >= 15 ? '#f59e0b' : '#ef4444', fontWeight: 600, marginLeft: 4 }}>
              {drivingPct}%
            </span>
          </div>
          <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
            keywords ranking on page 1 · last {days} days
          </div>
        </div>

        {activeBucket && (
          <button onClick={() => onBucketClick(null)}
            style={{ fontSize: 12, padding: '6px 12px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#f9fafb', color: '#374151', cursor: 'pointer', fontWeight: 500 }}
            onMouseEnter={e => { e.currentTarget.style.background = '#f3f4f6'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#f9fafb'; }}
          >
            Clear filter ×
          </button>
        )}
      </div>

      {/* The bar */}
      <div style={{ display: 'flex', height: 36, borderRadius: 8, overflow: 'hidden', gap: 2, background: '#f3f4f6' }}>
        {DIST_BUCKETS.map(b => {
          const data = dist[b.key] || { count: 0, pct: 0 };
          if (data.count === 0) return null;
          const isActive = activeBucket === b.key;
          const isDimmed = activeBucket && !isActive;
          const widthPct = Math.max(data.pct, 4);

          return (
            <div key={b.key}
              onClick={() => onBucketClick(b.key)}
              title={`${b.label} (${b.desc}): ${data.count} keywords`}
              style={{
                width: `${widthPct}%`,
                background: b.color,
                cursor: 'pointer',
                opacity: isDimmed ? 0.25 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: 13,
                fontWeight: 700,
                transition: 'opacity 0.15s, transform 0.15s',
                transform: isActive ? 'scaleY(1.08)' : 'scaleY(1)',
                transformOrigin: 'center',
                position: 'relative',
              }}
              onMouseEnter={e => { if (!isDimmed) e.currentTarget.style.transform = 'scaleY(1.12)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = isActive ? 'scaleY(1.08)' : 'scaleY(1)'; }}
            >
              {data.count >= 3 ? data.count : ''}
            </div>
          );
        })}
      </div>

      {/* Inline legend */}
      <div style={{ display: 'flex', gap: 20, marginTop: 12, flexWrap: 'wrap', fontSize: 12 }}>
        {DIST_BUCKETS.map(b => {
          const data = dist[b.key] || { count: 0 };
          const isActive = activeBucket === b.key;
          const isDimmed = activeBucket && !isActive;
          return (
            <div key={b.key}
              onClick={() => onBucketClick(b.key)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                cursor: 'pointer',
                opacity: isDimmed ? 0.4 : 1,
                fontWeight: isActive ? 700 : 500,
                color: isActive ? b.color : '#374151',
                userSelect: 'none',
              }}
            >
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: b.color, flexShrink: 0 }} />
              <span><strong style={{ color: isActive ? b.color : '#111827' }}>{data.count}</strong> {b.label.toLowerCase()}</span>
              <span style={{ color: '#9ca3af' }}>({b.desc})</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Filter Bar ────────────────────────────────────────────────────────────────
// New presets work on GSC data alone (Position, Clicks, Impressions, CTR).
// Each preset sets `mode` which is consumed in the `filtered` useMemo below.
const FILTER_PRESETS = [
  { id: 'top3',         label: '🎯 Top 3',        desc: 'Ranking 1\u20133' },
  { id: 'page1',        label: '⚡ Page 1',        desc: 'Ranking 4\u201310' },
  { id: 'quick_wins',   label: '🚀 Quick Wins',    desc: 'Page 2 with real impressions \u2014 close to break-through' },
  { id: 'lost_traffic', label: '💔 Lost Traffic',  desc: 'Showing in Google but nobody clicks' },
  { id: 'fix_ctr',      label: '✏️ Fix CTR',       desc: 'Top 10 but click-rate is poor \u2014 title or meta needs work' },
  { id: 'has_clicks',   label: '✓ Has Clicks',    desc: 'Keywords actually getting traffic' },
  { id: 'rule_cannibalization',   label: '⚠️ Cannibalization',    desc: 'Multiple URLs competing for same keyword', ruleId: 'cannibalization' },
  { id: 'rule_ctr_below_curve',   label: '🎯 CTR Below Curve',     desc: 'Snippet underperforms for this position', ruleId: 'ctr_below_curve' },
  { id: 'rule_striking_distance', label: '🚀 Striking Distance',   desc: 'Close to top 3 with real demand', ruleId: 'striking_distance' },
  { id: 'rule_position_drop',     label: '📉 Position Dropped',    desc: 'Recently fell 5+ positions', ruleId: 'position_drop' },
  { id: 'rule_demand_gap',        label: '🌱 Demand Gap',          desc: 'High volume but you rank past page 2', ruleId: 'demand_visibility_gap' },

];

function FilterBar({ filters, onChange, onReset, totalShowing, totalAll }) {
  const [open, setOpen] = useState(false);

  const activeCount = [
    !!filters.mode,
    filters.posMin > 1 || filters.posMax < 100,
    filters.volMin > 0,
    filters.kdMax < 100,
    filters.comp !== 'all',
    filters.oppMin > 0,
    filters.cpcMin > 0,
  ].filter(Boolean).length;

  return (
    <div style={{ marginBottom: 16 }}>
      {/* Preset chips */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
        <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>Filter:</span>
        {FILTER_PRESETS.map(p => {
          const isActive = filters.mode === p.id;
          return (
            <button key={p.id} title={p.desc}
              onClick={() => onChange({ ...filters, mode: isActive ? null : p.id })}
              style={{
                fontSize: 12, padding: '4px 10px', borderRadius: 20,
                border: `1.5px solid ${isActive ? '#667eea' : '#e2e8f0'}`,
                background: isActive ? '#667eea' : '#f8fafc',
                color: isActive ? '#fff' : '#374151',
                cursor: 'pointer', fontWeight: 500, whiteSpace: 'nowrap',
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => { if (!isActive) { e.target.style.background = '#e0e7ff'; } }}
              onMouseLeave={e => { if (!isActive) { e.target.style.background = '#f8fafc'; } }}
            >
              {p.label}
            </button>
          );
        })}
        <button onClick={() => setOpen(o => !o)}
          style={{ fontSize: 12, padding: '4px 10px', borderRadius: 20, border: `1.5px solid ${activeCount > 0 ? '#667eea' : '#e2e8f0'}`, background: activeCount > 0 ? '#ede9fe' : '#f8fafc', color: activeCount > 0 ? '#667eea' : '#374151', cursor: 'pointer', fontWeight: 600 }}>
          ⚙ Custom {activeCount > 0 ? `(${activeCount} active)` : ''}
        </button>
        {activeCount > 0 && (
          <button onClick={onReset}
            style={{ fontSize: 12, padding: '4px 10px', borderRadius: 20, border: '1.5px solid #fca5a5', background: '#fef2f2', color: '#ef4444', cursor: 'pointer', fontWeight: 600 }}>
            ✕ Reset
          </button>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 12, color: '#64748b' }}>
          Showing <strong>{totalShowing}</strong> of <strong>{totalAll}</strong> keywords
        </span>
      </div>

      {/* Custom filter panel */}
      {open && (
        <div style={{ background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '14px 18px', display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          {/* Position range */}
          <div>
            <label style={{ fontSize: 11, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 4 }}>POSITION</label>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="number" min="1" max="100" value={filters.posMin}
                onChange={e => onChange({ ...filters, posMin: +e.target.value })}
                style={{ width: 55, padding: '4px 8px', border: '1.5px solid #e2e8f0', borderRadius: 6, fontSize: 13 }} />
              <span style={{ color: '#94a3b8', fontSize: 12 }}>to</span>
              <input type="number" min="1" max="200" value={filters.posMax}
                onChange={e => onChange({ ...filters, posMax: +e.target.value })}
                style={{ width: 55, padding: '4px 8px', border: '1.5px solid #e2e8f0', borderRadius: 6, fontSize: 13 }} />
            </div>
          </div>

          {/* Min volume */}
          <div>
            <label style={{ fontSize: 11, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 4 }}>MIN VOLUME</label>
            <select value={filters.volMin} onChange={e => onChange({ ...filters, volMin: +e.target.value })}
              style={{ padding: '4px 8px', border: '1.5px solid #e2e8f0', borderRadius: 6, fontSize: 13, background: '#fff' }}>
              <option value={0}>Any</option>
              <option value={10}>10+</option>
              <option value={100}>100+</option>
              <option value={500}>500+</option>
              <option value={1000}>1,000+</option>
              <option value={5000}>5,000+</option>
            </select>
          </div>

          {/* Max KD */}
          <div>
            <label style={{ fontSize: 11, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 4 }}>MAX KD</label>
            <select value={filters.kdMax} onChange={e => onChange({ ...filters, kdMax: +e.target.value })}
              style={{ padding: '4px 8px', border: '1.5px solid #e2e8f0', borderRadius: 6, fontSize: 13, background: '#fff' }}>
              <option value={100}>Any</option>
              <option value={20}>Easy (≤20)</option>
              <option value={40}>Low (≤40)</option>
              <option value={60}>Medium (≤60)</option>
              <option value={80}>Hard (≤80)</option>
            </select>
          </div>

          {/* Competition */}
          <div>
            <label style={{ fontSize: 11, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 4 }}>COMPETITION</label>
            <select value={filters.comp} onChange={e => onChange({ ...filters, comp: e.target.value })}
              style={{ padding: '4px 8px', border: '1.5px solid #e2e8f0', borderRadius: 6, fontSize: 13, background: '#fff' }}>
              <option value="all">All</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>

          {/* Min CPC */}
          <div>
            <label style={{ fontSize: 11, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 4 }}>MIN CPC</label>
            <select value={filters.cpcMin || 0} onChange={e => onChange({ ...filters, cpcMin: +e.target.value })}
              style={{ padding: '4px 8px', border: '1.5px solid #e2e8f0', borderRadius: 6, fontSize: 13, background: '#fff' }}>
              <option value={0}>Any</option>
              <option value={1}>$1+</option>
              <option value={5}>$5+</option>
              <option value={10}>$10+</option>
              <option value={20}>$20+</option>
            </select>
          </div>

          {/* Min opportunity score */}
          <div>
            <label style={{ fontSize: 11, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 4 }}>MIN OPP SCORE</label>
            <select value={filters.oppMin} onChange={e => onChange({ ...filters, oppMin: +e.target.value })}
              style={{ padding: '4px 8px', border: '1.5px solid #e2e8f0', borderRadius: 6, fontSize: 13, background: '#fff' }}>
              <option value={0}>Any</option>
              <option value={40}>Fair (40+)</option>
              <option value={60}>Good (60+)</option>
              <option value={70}>Great (70+)</option>
              <option value={80}>Excellent (80+)</option>
            </select>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Column preset toggle ──────────────────────────────────────────────────────
// ── Action classifier — single source of truth ──────────────────────────────
// Returns one of the action labels based on GSC data alone (no DataForSEO needed)
const ACTION_LABELS = {
  TOP_3:        { label: '🎯 Top 3',        color: '#10b981', bg: '#d1fae5', priority: 5 },
  DEFEND:       { label: '🛡 Defend',       color: '#10b981', bg: '#d1fae5', priority: 5 },
  FIX_CTR:      { label: '✏️ Fix CTR',      color: '#ea580c', bg: '#ffedd5', priority: 2 },
  PAGE_1_CTR:   { label: '⚡ Improve CTR',   color: '#d97706', bg: '#fef3c7', priority: 3 },
  QUICK_WIN:    { label: '⚡ Quick Win',     color: '#3b82f6', bg: '#dbeafe', priority: 4 },
  PUSH_TO_P1:   { label: '🚀 Push to P1',   color: '#d97706', bg: '#fef9c3', priority: 3 },
  LOST_TRAFFIC: { label: '💔 Lost Traffic', color: '#dc2626', bg: '#fee2e2', priority: 1 },
  CONTENT_GAP:  { label: '🔍 Content Gap',  color: '#7c3aed', bg: '#f3e8ff', priority: 4 },
  MONITOR:      { label: '· Monitor',       color: '#94a3b8', bg: '#f1f5f9', priority: 9 },
};

function classifyKeyword(k) {
  const pos = k.avg_position || 0;
  const clicks = k.total_clicks || 0;
  const impressions = k.total_impressions || 0;
  const ctr = k.avg_ctr || 0;  // GSC returns 0–1

  // Signal floor: below 10 impressions with no clicks, no verdict is statistically defensible
  if (impressions < 10 && clicks < 1) return ACTION_LABELS.MONITOR;

  // Top 3 with low CTR — title/snippet problem (highest priority fix)
  if (pos <= 3 && impressions >= 50 && ctr < 0.02) return ACTION_LABELS.FIX_CTR;
  // Top 3 doing fine — defend
  if (pos <= 3) return ACTION_LABELS.TOP_3;
  // Page 1 (4-10) with poor CTR
  if (pos <= 10 && impressions >= 50 && ctr < 0.02) return ACTION_LABELS.PAGE_1_CTR;
  // Page 1 (4-10) with traffic
  if (pos <= 10) return ACTION_LABELS.QUICK_WIN;
  // Page 2 (11-20) with real impressions — close to break-through
  if (pos <= 20 && impressions >= 50) return ACTION_LABELS.PUSH_TO_P1;
  // Has impressions but no clicks (any deep position)
  if (impressions >= 50 && clicks === 0) return ACTION_LABELS.LOST_TRAFFIC;
  // Page 3+ with high impressions but ranking deep
  if (pos > 20 && impressions >= 100) return ACTION_LABELS.CONTENT_GAP;
  // Default — no specific action
  return ACTION_LABELS.MONITOR;
}

const COL_PRESETS = {
  seo:  { label: '🔍 SEO View',  cols: ['action', 'position', 'clicks', 'impressions', 'ctr'] },
  ppc:  { label: '💰 PPC View',  cols: ['action', 'position', 'clicks', 'volume', 'cpc', 'competition', 'ctr'] },
  full: { label: '📊 Full View', cols: ['action', 'position', 'clicks', 'impressions', 'ctr', 'volume', 'kd', 'cpc', 'trend'] },
};

// ── Main Keywords component ───────────────────────────────────────────────────
const DEFAULT_FILTERS = { mode: null, posMin: 1, posMax: 100, volMin: 0, kdMax: 100, comp: 'all', oppMin: 0, cpcMin: 0 };

function Keywords({ websiteId, globalDays = 28 }) {
  const [keywords, setKeywords]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal]             = useState(0);
  const [search, setSearch]           = useState('');
  const [sortCol, setSortCol]         = useState('total_impressions');
  const [sortDir, setSortDir]         = useState('desc');
  const [expandedRow, setExpandedRow] = useState(null);
  const [trendData, setTrendData]     = useState({});
  const [trendLoading, setTrendLoading] = useState({});
  const [exporting, setExporting]     = useState(false);
  const [volumeMap, setVolumeMap]     = useState({});
  const [volLoading, setVolLoading]   = useState(false);
  const [stats, setStats]             = useState(null);
  const [activeDistBucket, setActiveDistBucket] = useState(null);
  const [serpKeyword, setSerpKeyword] = useState(null);
  const [showLowSignal, setShowLowSignal] = useState(false);
  const [hiddenLowSignal, setHiddenLowSignal] = useState(0);
  const [activeQueue, setActiveQueue] = useState(null);
  const [filters, setFilters]         = useState(DEFAULT_FILTERS);
  const [colPreset, setColPreset]     = useState('seo');
  const days = globalDays;
  const perPage = 50;
  const searchTimer = useRef(null);

  const loadKeywords = useCallback(async () => {
    if (!websiteId) return;
    setLoading(true);
    try {
      // Look up if the active preset is a rule-based filter (server-side)
      const activePreset = FILTER_PRESETS.find(p => p.id === filters.mode);
      const ruleId = activePreset && activePreset.ruleId ? activePreset.ruleId : '';
      const data = await api.getKeywords(websiteId, activeQueue ? 1 : currentPage, activeQueue ? 500 : perPage, sortCol, days, activeDistBucket || '', ruleId, !showLowSignal);
      const kws = (data.keywords || []).filter(k => !activeQueue || k.queue === activeQueue);
      setKeywords(kws);
      setTotal(data.total || 0);
      setHiddenLowSignal(data.hidden_low_signal || 0);
      if (kws.length > 0) {
        setVolLoading(true);
        const volData = await fetchVolumeData(kws.map(k => k.keyword));
        setVolumeMap(prev => ({ ...prev, ...volData }));
        setVolLoading(false);
      }
      // Fetch position distribution stats
      try {
        const statsData = await api.getKeywordStats(websiteId, days);
        setStats(statsData);
      } catch (e) {
        console.error('Stats fetch error:', e);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [websiteId, currentPage, perPage, sortCol, days, activeDistBucket, filters.mode, showLowSignal, activeQueue]);

  useEffect(() => { loadKeywords(); }, [loadKeywords]);

  const handleSearch = (val) => {
    setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setCurrentPage(1); loadKeywords(); }, 300);
  };

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortCol(col); setSortDir('desc'); }
    setCurrentPage(1);
  };

  const toggleRow = async (kw) => {
    const key = kw.keyword;
    if (expandedRow === key) { setExpandedRow(null); return; }
    setExpandedRow(key);
    if (trendData[key]) return;
    setTrendLoading(p => ({ ...p, [key]: true }));
    try {
      const result = await api.getKeywordTrends(websiteId, key);
      setTrendData(p => ({ ...p, [key]: result }));
    } catch (e) {
      setTrendData(p => ({ ...p, [key]: { error: true } }));
    } finally {
      setTrendLoading(p => ({ ...p, [key]: false }));
    }
  };

  // ── Apply filters + search client-side ────────────────────────────────────
  // Prefer the inline volume data from the keywords API response (faster, no
  // round-trip). Fall back to the legacy volumeMap fetch only if the API
  // didn't include it (older endpoints, partial cache misses).
  const enrichedKeywords = useMemo(() => {
    return keywords.map(kw => {
      const volKey = kw.keyword.toLowerCase().trim();
      // If the keywords endpoint returned volume inline, use that. Otherwise look
      // up in the legacy volumeMap (which is still populated as a fallback path).
      const inline = (kw.volume !== undefined && kw.volume !== null);
      const vol = inline
        ? {
            volume: kw.volume,
            cpc: kw.cpc,
            keyword_difficulty: kw.keyword_difficulty,
            competition: kw.competition,
            monthly_searches: kw.weekly_positions
              ? kw.weekly_positions.map((p, i) => ({ year: 0, month: 4 - i, volume: p }))
              : [],
          }
        : (volumeMap[volKey] || {});
      const score = computeOpportunityScore(vol.volume, vol.keyword_difficulty, kw.avg_position, vol.cpc);
      return { ...kw, _vol: vol, _score: score };
    });
  }, [keywords, volumeMap]);

  const filtered = useMemo(() => {
    return enrichedKeywords.filter(kw => {
      const pos = kw.avg_position || 0;
      const vol = kw._vol.volume || 0;
      const kd  = kw._vol.keyword_difficulty !== null && kw._vol.keyword_difficulty !== undefined ? kw._vol.keyword_difficulty : 100;
      const comp = kw._vol.competition || 'unknown';
      const cpc = kw._vol.cpc || 0;
      const score = kw._score;
      const clicks = kw.total_clicks || 0;
      const impressions = kw.total_impressions || 0;
      const ctr = kw.avg_ctr || 0;  // 0\u20131

      // Search box
      if (search && !kw.keyword.toLowerCase().includes(search.toLowerCase())) return false;

      // Mode-based GSC presets (work on GSC data alone)
      switch (filters.mode) {
        case 'top3':
          if (!(pos >= 1 && pos <= 3)) return false;
          break;
        case 'page1':
          if (!(pos > 3 && pos <= 10)) return false;
          break;
        case 'quick_wins':
          if (!(pos > 10 && pos <= 20 && impressions >= 50)) return false;
          break;
        case 'lost_traffic':
          if (!(impressions >= 50 && clicks === 0)) return false;
          break;
        case 'fix_ctr':
          if (!(pos >= 1 && pos <= 10 && impressions >= 50 && ctr < 0.02)) return false;
          break;
        case 'has_clicks':
          if (!(clicks > 0)) return false;
          break;
        default:
          break;
      }

      // Custom filters (still work for power users with DataForSEO data)
      if (pos < filters.posMin || pos > filters.posMax) return false;
      if (vol < filters.volMin) return false;
      if (kd > filters.kdMax) return false;
      if (filters.comp !== 'all' && comp !== filters.comp) return false;
      if (cpc < (filters.cpcMin || 0)) return false;
      if (score < filters.oppMin) return false;
      return true;
    });
  }, [enrichedKeywords, filters, search]);

  // Sort client-side for opp score; server handles other sorts
  const sorted = useMemo(() => {
    if (sortCol === 'opp_score') {
      return [...filtered].sort((a, b) => sortDir === 'desc' ? b._score - a._score : a._score - b._score);
    }
    return filtered;
  }, [filtered, sortCol, sortDir]);

  // Auto-hide DataForSEO columns when no data exists across visible rows.
  // Honest: don't show empty columns just because the preset includes them.
  const cols = useMemo(() => {
    const baseCols = COL_PRESETS[colPreset].cols;
    if (sorted.length === 0) return baseCols;
    const hasAnyVolume = sorted.some(kw => (kw._vol?.volume || 0) > 0);
    const hasAnyKd     = sorted.some(kw => kw._vol?.keyword_difficulty !== null && kw._vol?.keyword_difficulty !== undefined);
    const hasAnyCpc    = sorted.some(kw => (kw._vol?.cpc || 0) > 0);
    const hasAnyTrend  = sorted.some(kw => Array.isArray(kw._vol?.monthly_searches) && kw._vol.monthly_searches.length > 0);
    return baseCols.filter(c => {
      if (c === 'volume' && !hasAnyVolume) return false;
      if (c === 'kd'     && !hasAnyKd)     return false;
      if (c === 'cpc'    && !hasAnyCpc)    return false;
      if (c === 'trend'  && !hasAnyTrend)  return false;
      return true;
    });
  }, [colPreset, sorted]);
  const showCol = c => cols.includes(c);

  const handleExport = async () => {
    setExporting(true);
    try {
      const data = await api.exportKeywordsCSV(websiteId);
      const rows = data.keywords || [];
      const csv = [
        'Keyword,Opp Score,Volume,KD,CPC,Competition,Avg Position,Total Clicks,Total Impressions,Avg CTR',
        ...rows.map(k => {
          const vol = volumeMap[k.keyword?.toLowerCase()] || {};
          const score = computeOpportunityScore(vol.volume, vol.keyword_difficulty, k.avg_position, vol.cpc);
          return `"${k.keyword}",${score},${vol.volume||''},${vol.keyword_difficulty||''},${vol.cpc||''},${vol.competition||''},${(k.avg_position||0).toFixed(1)},${k.total_clicks||0},${k.total_impressions||0},${((k.avg_ctr||0)*100).toFixed(2)}%`;
        })
      ].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url;
      a.download = `keywords-${websiteId}-${new Date().toISOString().slice(0,10)}.csv`;
      a.click(); URL.revokeObjectURL(url);
    } catch (e) {
      alert('Export failed: ' + e.message);
    } finally {
      setExporting(false);
    }
  };

  const posColor = p => p <= 3 ? '#16a34a' : p <= 10 ? '#22c55e' : p <= 20 ? '#f59e0b' : '#ef4444';
  const posLabel = p => p <= 3 ? 'top3' : p <= 10 ? 'good' : p <= 20 ? 'warning' : 'bad';
  const SortIcon = ({ col }) => sortCol !== col
    ? <span style={{ opacity: 0.3, marginLeft: 4 }}>↕</span>
    : <span style={{ marginLeft: 4 }}>{sortDir === 'desc' ? '↓' : '↑'}</span>;

  if (!websiteId) return (
    <div className="empty-state">
      <div className="empty-icon">📋</div>
      <h3>No Website Selected</h3>
      <p>Please select a website to view keywords</p>
    </div>
  );

  return (
    <div className="card">
      {/* Header */}
      <div className="card-header">
        <h2>📋 All Keywords {total > 0 && <span style={{ fontSize: 14, fontWeight: 400, color: '#64748b' }}>({total.toLocaleString()} total)</span>}</h2>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          
          {/* Column preset toggle */}
          <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: 8, padding: 3, gap: 2 }}>
            {Object.entries(COL_PRESETS).map(([key, { label }]) => (
              <button key={key} onClick={() => setColPreset(key)}
                style={{ fontSize: 12, padding: '4px 10px', borderRadius: 6, border: 'none', cursor: 'pointer', fontWeight: 600, background: colPreset === key ? '#fff' : 'transparent', color: colPreset === key ? '#374151' : '#94a3b8', boxShadow: colPreset === key ? '0 1px 3px rgba(0,0,0,0.1)' : 'none' }}>
                {label}
              </button>
            ))}
          </div>
          {/* Search */}
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: 14 }}>🔍</span>
            <input value={search} onChange={e => handleSearch(e.target.value)} placeholder="Search keywords..."
              style={{ paddingLeft: 32, paddingRight: 12, paddingTop: 8, paddingBottom: 8, border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, width: 200 }} />
          </div>
          <button className="btn-secondary" onClick={handleExport} disabled={exporting} style={{ fontSize: 13 }}>
            {exporting ? '⏳ Exporting...' : '📊 Export CSV'}
          </button>
        </div>
      </div>

      <div className="card-body">
        {loading ? (
          <div className="loading"><div className="spinner"></div><p>Loading keywords...</p></div>
        ) : (
          <>
            {/* Verdict header: stat cards + position bar + queue rail */}
            {stats && (
              <QueueHeader
                stats={stats}
                days={days}
                activeBucket={activeDistBucket}
                onBucketClick={(bucket) => {
                  setActiveDistBucket(prev => prev === bucket ? null : bucket);
                  setActiveQueue(null);
                  setCurrentPage(1);
                }}
                activeQueue={activeQueue}
                onQueueClick={(q) => {
                  setActiveQueue(prev => prev === q ? null : q);
                  setActiveDistBucket(null);
                  setCurrentPage(1);
                }}
              />
            )}

            {/* Filter bar */}
            <FilterBar
              filters={filters}
              onChange={setFilters}
              onReset={() => setFilters(DEFAULT_FILTERS)}
              totalShowing={sorted.length}
              totalAll={keywords.length}
            />

            {(hiddenLowSignal > 0 || showLowSignal) && (
              <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                {showLowSignal
                  ? 'Including low-signal keywords (fewer than 10 impressions and no clicks). These are statistically unreliable.'
                  : `${hiddenLowSignal.toLocaleString()} low-signal keywords hidden (fewer than 10 impressions, no clicks).`}
                <button
                  onClick={() => { setShowLowSignal(v => !v); setCurrentPage(1); }}
                  style={{ fontSize: 12, padding: '2px 10px', borderRadius: 12, border: '1px solid #cbd5e1', background: '#fff', color: '#64748b', cursor: 'pointer', fontWeight: 600 }}
                >
                  {showLowSignal ? 'Hide them' : 'Show them'}
                </button>
              </div>
            )}

            {volLoading && (
              <div style={{ fontSize: 12, color: '#2563eb', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                ⏳ Loading volume data from DataForSEO...
              </div>
            )}

            {sorted.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">🔍</div>
                <h3>No keywords match your filters</h3>
                <p>Try adjusting the filters above or <button onClick={() => setFilters(DEFAULT_FILTERS)} style={{ color: '#667eea', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 600 }}>reset all filters</button></p>
              </div>
            ) : (
              <>
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th style={{ cursor: 'pointer', minWidth: 200 }} onClick={() => handleSort('keyword')}>Keyword <SortIcon col="keyword" /></th>
                        {showCol('action')      && <th style={{ width: 130 }}>Action</th>}
                        {showCol('opp')         && <th style={{ cursor: 'pointer', width: 110 }} onClick={() => handleSort('opp_score')}>Opp Score <SortIcon col="opp_score" /></th>}
                        {showCol('volume')      && <th style={{ cursor: 'pointer', width: 90  }} onClick={() => handleSort('volume')}>Volume <SortIcon col="volume" /></th>}
                        {showCol('trend')       && <th style={{ width: 130 }}>Trend (12m)</th>}
                        {showCol('kd')          && <th style={{ width: 110 }}>KD</th>}
                        {showCol('cpc')         && <th style={{ width: 80  }}>CPC</th>}
                        {showCol('competition') && <th style={{ width: 100 }}>Competition</th>}
                        {showCol('position')    && <th title="Average position across the impressions Google recorded in this date range — not a live rank check. Low-impression rows are statistically weak." style={{ cursor: 'pointer', width: 110 }} onClick={() => handleSort('avg_position')}>GSC Position <SortIcon col="avg_position" /></th>}
                        {showCol('clicks')      && <th style={{ cursor: 'pointer', width: 80  }} onClick={() => handleSort('total_clicks')}>Clicks <SortIcon col="total_clicks" /></th>}
                        {showCol('impressions') && <th style={{ cursor: 'pointer', width: 110 }} onClick={() => handleSort('total_impressions')}>Impressions <SortIcon col="total_impressions" /></th>}
                        {showCol('ctr')         && <th style={{ width: 70 }}>CTR</th>}
                        <th style={{ width: 90 }}>SERP</th>
                        <th style={{ width: 30 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {sorted.map((kw, idx) => {
                        const pos = kw.avg_position || 0;
                        const isExpanded = expandedRow === kw.keyword;
                        const trend = trendData[kw.keyword];
                        const isLoadingTrend = trendLoading[kw.keyword];
                        const vol = kw._vol;
                        const score = kw._score;
                        const scoreC = oppScoreColor(score);
                        const kd = vol.keyword_difficulty;
                        const kdC = kdColor(kd);
                        const compC = compColor(vol.competition);
                        const fmtVol = formatVolume(vol.volume);
                        const colSpanCount = cols.length + 2; // keyword + expand col

                        return (
                          <React.Fragment key={idx}>
                            <tr onClick={() => toggleRow(kw)}
                              style={{ cursor: 'pointer', background: isExpanded ? '#f0f4ff' : idx % 2 === 0 ? '#fff' : '#fafafa' }}>

                              {/* Keyword */}
                              <td className="keyword-cell" style={{ fontWeight: isExpanded ? 700 : 500 }}>
                                <span>{kw.keyword}</span>
                                {kw.queue && kw.queue !== 'watching' && QUEUE_META[kw.queue] && (
                                  <span style={{ marginLeft: 8, fontSize: 10.5, fontWeight: 700, color: QUEUE_META[kw.queue].color, background: QUEUE_META[kw.queue].bg, border: '1px solid ' + QUEUE_META[kw.queue].border, padding: '1px 7px', borderRadius: 9, whiteSpace: 'nowrap' }}>
                                    {QUEUE_META[kw.queue].icon} {QUEUE_META[kw.queue].label}
                                  </span>
                                )}
                                <OpportunityBadge position={pos} volume={vol.volume} />
                              </td>

                              {/* Action / Recommendation — prefers API recommendation if present, falls back to GSC-only classifier */}
                              {showCol('action') && (() => {
                                // Color palette by rule_id (matches severity intent)
                                const RULE_COLORS = {
                                  position_drop:        { bg: '#fee2e2', color: '#991b1b' },  // urgent red
                                  cannibalization:      { bg: '#fef3c7', color: '#92400e' },  // amber
                                  ctr_below_curve:      { bg: '#fef9c3', color: '#854d0e' },  // yellow
                                  featured_snippet:     { bg: '#f3e8ff', color: '#6b21a8' },  // purple
                                  striking_distance:    { bg: '#dbeafe', color: '#1e40af' },  // blue
                                  demand_visibility_gap:{ bg: '#dcfce7', color: '#166534' },  // green
                                };
                                
                                const rec = kw.recommendation;
                                if (rec && rec.rule_id) {
                                  const c = RULE_COLORS[rec.rule_id] || { bg: '#e0e7ff', color: '#3730a3' };
                                  return (
                                    <td>
                                      <span 
                                        title={rec.diagnosis}
                                        style={{ display: 'inline-block', fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 6, background: c.bg, color: c.color, whiteSpace: 'nowrap', cursor: 'help' }}
                                      >
                                        {rec.title}
                                      </span>
                                    </td>
                                  );
                                }
                                
                                // Fallback to legacy classifier when no recommendation (low-signal keyword, etc.)
                                const action = classifyKeyword(kw);
                                return (
                                  <td>
                                    <span style={{ display: 'inline-block', fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 6, background: action.bg, color: action.color, whiteSpace: 'nowrap' }}>
                                      {action.label}
                                    </span>
                                  </td>
                                );
                              })()}

                              {/* Opportunity score */}
                              {showCol('opp') && (
                                <td>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: scoreC.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                      <span style={{ fontSize: 12, fontWeight: 800, color: scoreC.text }}>{score}</span>
                                    </div>
                                    <span style={{ fontSize: 10, color: scoreC.text, fontWeight: 600 }}>{scoreC.label}</span>
                                  </div>
                                </td>
                              )}

                              {/* Volume */}
                              {showCol('volume') && (
                                <td>
                                  {volLoading && fmtVol === null
                                    ? <span style={{ display: 'inline-block', width: 40, height: 12, background: '#e2e8f0', borderRadius: 4 }} />
                                    : fmtVol
                                      ? <span style={{ fontWeight: 700, color: volumeColor(vol.volume), fontSize: 14 }}>{fmtVol}</span>
                                      : <span title="No volume data from DataForSEO for this keyword" style={{ fontSize: 13, color: '#cbd5e1', cursor: 'help' }}>—</span>
                                  }
                                </td>
                              )}

                              {/* Sparkline */}
                              {showCol('trend') && (
                                <td>
                                  {volLoading && !vol.monthly_searches?.length
                                    ? <span style={{ display: 'inline-block', width: 80, height: 12, background: '#e2e8f0', borderRadius: 4 }} />
                                    : <Sparkline data={vol.monthly_searches || []} />
                                  }
                                </td>
                              )}

                              {/* KD */}
                              {showCol('kd') && (
                                <td>
                                  {kd !== undefined && kd !== null
                                    ? <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                        <div style={{ width: 36, height: 5, background: '#e2e8f0', borderRadius: 3, overflow: 'hidden' }}>
                                          <div style={{ width: `${kd}%`, height: '100%', background: kdC.text, borderRadius: 3 }} />
                                        </div>
                                        <span style={{ fontSize: 11, fontWeight: 700, padding: '1px 5px', borderRadius: 4, background: kdC.bg, color: kdC.text }}>{kd}</span>
                                        <span style={{ fontSize: 10, color: '#94a3b8' }}>{kdLabel(kd)}</span>
                                      </div>
                                    : <span style={{ color: '#cbd5e1' }}>—</span>
                                  }
                                </td>
                              )}

                              {/* CPC */}
                              {showCol('cpc') && (
                                <td>
                                  <span style={{ fontWeight: 600, color: vol.cpc > 5 ? '#16a34a' : vol.cpc > 1 ? '#d97706' : '#94a3b8', fontSize: 13 }}>
                                    {vol.cpc !== null && vol.cpc !== undefined && vol.cpc > 0 ? `$${vol.cpc.toFixed(2)}` : '—'}
                                  </span>
                                </td>
                              )}

                              {/* Competition */}
                              {showCol('competition') && (
                                <td>
                                  {vol.competition && vol.competition !== 'unknown'
                                    ? <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 10, background: compC.bg, color: compC.text, textTransform: 'capitalize' }}>{vol.competition}</span>
                                    : <span style={{ color: '#cbd5e1' }}>—</span>
                                  }
                                </td>
                              )}

                              {/* GSC data */}
                              {showCol('position') && (
                                <td>
                                  {(kw.total_impressions || 0) < 10 ? (
                                    <span
                                      title={`Avg position ${pos.toFixed(1)} from only ${kw.total_impressions || 0} impression(s) in this range — too little data to treat as a ranking. Google showed this page rarely; when it did, this was its average spot.`}
                                      style={{ color: '#94a3b8', fontWeight: 500, fontSize: 13, cursor: 'help', whiteSpace: 'nowrap' }}
                                    >
                                      {pos.toFixed(1)} <span style={{ fontSize: 10, background: '#f1f5f9', color: '#64748b', padding: '2px 5px', borderRadius: 4, fontWeight: 600 }}>low data</span>
                                    </span>
                                  ) : (
                                    <span className={`position-badge ${posLabel(pos)}`} style={{ color: posColor(pos), fontWeight: 700, fontSize: 15 }}>{pos.toFixed(1)}</span>
                                  )}
                                </td>
                              )}
                              {showCol('clicks')      && <td style={{ fontWeight: 500 }}>{(kw.total_clicks || 0).toLocaleString()}</td>}
                              {showCol('impressions') && <td style={{ fontWeight: 500 }}>{(kw.total_impressions || 0).toLocaleString()}</td>}
                              {showCol('ctr')         && <td style={{ color: '#64748b' }}>{((kw.avg_ctr || 0) * 100).toFixed(2)}%</td>}
                              <td onClick={e => { e.stopPropagation(); setSerpKeyword(kw.keyword); }} style={{ textAlign: 'center' }}>
                                <span style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, background: '#f0f4ff', color: '#667eea', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', border: '1px solid #c7d2fe' }}>
                                  🔍 SERP
                                </span>
                              </td>
                              <td style={{ textAlign: 'center', color: '#94a3b8', fontSize: 12 }}>{isExpanded ? '▲' : '▼'}</td>
                            </tr>

                            {/* Expanded row */}
                            {isExpanded && (
                              <tr style={{ background: '#f0f4ff' }}>
                                <td colSpan={colSpanCount} style={{ padding: '16px 20px', borderBottom: '2px solid #e2e8f0' }}>
                                  {/* Page Coverage (crawl-data join) */}
                                  {(() => {
                                    const cov = kw.coverage;
                                    if (!cov) return null;
                                    const COV = {
                                      dedicated:   { label: 'Page Coverage: Dedicated',   bg: '#f0fdf4', border: '#86efac', accent: '#166534', icon: '\u2705' },
                                      partial:     { label: 'Page Coverage: Partial',     bg: '#fffbeb', border: '#fcd34d', accent: '#92400e', icon: '\u26a1' },
                                      mismatch:    { label: 'Targeting Mismatch',         bg: '#fff7ed', border: '#fdba74', accent: '#9a3412', icon: '\u26a0\ufe0f' },
                                      content_gap: { label: 'Content Gap',                bg: '#fef2f2', border: '#fca5a5', accent: '#991b1b', icon: '\ud83d\udd0d' },
                                      brand:       { label: 'Brand Query',                bg: '#fbf8f3', border: '#b9c096', accent: '#3d4128', icon: '\U0001f3f7\ufe0f' },
                                      no_audit:    { label: 'Coverage Unknown',           bg: '#f8fafc', border: '#cbd5e1', accent: '#475569', icon: '\u2754' },
                                    };
                                    const c = COV[cov.status] || COV.no_audit;
                                    const lowVisibility = (kw.total_impressions || 0) < 10;
                                    let explanation;
                                    if (cov.status === 'dedicated') {
                                      explanation = lowVisibility
                                        ? 'A page on this site targets this keyword. Low visibility here is an authority problem, not a missing-content problem \u2014 strengthen internal links and citations rather than creating new pages.'
                                        : 'A page on this site targets this keyword and is being seen for it.';
                                    } else if (cov.status === 'partial') {
                                      explanation = 'A related page partially covers this keyword. Consider whether it deserves its own dedicated page or a stronger focus on the existing one.';
                                    } else if (cov.status === 'mismatch') {
                                      explanation = 'A dedicated page exists for this keyword, but Google is ranking a different URL. Review internal linking and intent alignment so the right page wins.';
                                    } else if (cov.status === 'brand') {
                                      explanation = 'Brand query - searchers are looking for this site by name. No content action needed.';
                                    } else if (cov.status === 'content_gap') {
                                      explanation = 'No crawled page on this site targets this keyword. If the demand justifies it, this is a genuine content opportunity.';
                                    } else {
                                      explanation = 'Run a Site Audit to enable coverage analysis \u2014 it maps every keyword to the page (if any) that targets it.';
                                    }
                                    return (
                                      <div style={{ marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid #e2e8f0' }}>
                                        <div style={{ background: c.bg, border: `1px solid ${c.border}`, borderLeft: `4px solid ${c.accent}`, borderRadius: 8, padding: '10px 14px' }}>
                                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                            <span style={{ fontSize: 14 }}>{c.icon}</span>
                                            <span style={{ fontSize: 13, fontWeight: 700, color: c.accent }}>{c.label}</span>
                                            {typeof cov.matched_score === 'number' && cov.matched_score > 0 && (
                                              <span style={{ fontSize: 10, fontWeight: 600, color: c.accent, opacity: 0.7, padding: '2px 6px', background: 'rgba(255,255,255,0.6)', borderRadius: 4 }}>
                                                match {Math.round(cov.matched_score * 100)}%
                                              </span>
                                            )}
                                          </div>
                                          <div style={{ fontSize: 12.5, color: '#374151', lineHeight: 1.5 }}>{explanation}</div>
                                          {cov.matched_url && (
                                            <div style={{ fontSize: 12, marginTop: 6 }}>
                                              <span style={{ fontWeight: 700, color: c.accent }}>Target page: </span>
                                              <a href={cov.matched_url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{ color: c.accent, wordBreak: 'break-all' }}>
                                                {cov.matched_url}
                                              </a>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })()}

                                  
                                  {/* ── Recommendations (top section, full width) ────────────── */}
                                  {Array.isArray(kw.all_recommendations) && kw.all_recommendations.length > 0 && (() => {
                                    const RULE_COLORS = {
                                      position_drop:        { bg: '#fef2f2', border: '#fca5a5', accent: '#991b1b', icon: '📉' },
                                      cannibalization:      { bg: '#fffbeb', border: '#fcd34d', accent: '#92400e', icon: '⚠️' },
                                      ctr_below_curve:      { bg: '#fefce8', border: '#facc15', accent: '#854d0e', icon: '🎯' },
                                      featured_snippet:     { bg: '#faf5ff', border: '#c4b5fd', accent: '#6b21a8', icon: '⭐' },
                                      striking_distance:    { bg: '#eff6ff', border: '#93c5fd', accent: '#1e40af', icon: '🚀' },
                                      demand_visibility_gap:{ bg: '#f0fdf4', border: '#86efac', accent: '#166534', icon: '🌱' },
                                    };
                                    const recs = kw.all_recommendations.slice(0, 3);
                                    return (
                                      <div style={{ marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid #e2e8f0' }}>
                                        <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                                          Recommendations ({kw.all_recommendations.length})
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                          {recs.map((rec, ridx) => {
                                            const c = RULE_COLORS[rec.rule_id] || { bg: '#f8fafc', border: '#cbd5e1', accent: '#475569', icon: '💡' };
                                            const isPrimary = ridx === 0;
                                            return (
                                              <div key={rec.rule_id} style={{ background: c.bg, border: `1px solid ${c.border}`, borderLeft: `4px solid ${c.accent}`, borderRadius: 8, padding: isPrimary ? '12px 14px' : '10px 12px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: isPrimary ? 8 : 4 }}>
                                                  <span style={{ fontSize: isPrimary ? 16 : 14 }}>{c.icon}</span>
                                                  <span style={{ fontSize: isPrimary ? 14 : 13, fontWeight: 700, color: c.accent }}>{rec.title}</span>
                                                  <span style={{ fontSize: 10, fontWeight: 600, color: c.accent, opacity: 0.7, padding: '2px 6px', background: 'rgba(255,255,255,0.6)', borderRadius: 4 }}>
                                                    Priority {rec.severity}
                                                  </span>
                                                </div>
                                                <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.5, marginBottom: 6 }}>
                                                  {rec.diagnosis}
                                                </div>
                                                
                                                {/* Cannibalization: show the actual competing URLs */}
                                                {rec.rule_id === 'cannibalization' && rec.evidence && Array.isArray(rec.evidence.competing_urls) && rec.evidence.competing_urls.length > 0 && (
                                                  <div style={{ fontSize: 12, marginTop: 6, marginBottom: 6, padding: '8px 10px', background: 'rgba(255,255,255,0.7)', borderRadius: 6, border: '1px dashed ' + c.border }}>
                                                    <div style={{ fontWeight: 700, color: c.accent, marginBottom: 4 }}>Competing URLs:</div>
                                                    <ul style={{ margin: 0, paddingLeft: 18, color: '#374151', lineHeight: 1.6 }}>
                                                      {rec.evidence.competing_urls.slice(0, 10).map(url => (
                                                        <li key={url} style={{ wordBreak: 'break-all' }}>
                                                          <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: c.accent, textDecoration: 'none' }} onClick={e => e.stopPropagation()}>
                                                            {url}
                                                          </a>
                                                        </li>
                                                      ))}
                                                    </ul>
                                                  </div>
                                                )}
                                                
                                                {isPrimary && (
                                                  <>
                                                    <div style={{ fontSize: 12, color: '#1f2937', marginTop: 8, padding: '8px 10px', background: 'rgba(255,255,255,0.7)', borderRadius: 6 }}>
                                                      <span style={{ fontWeight: 700, color: c.accent }}>What to do: </span>
                                                      {rec.action}
                                                    </div>
                                                    <div style={{ fontSize: 12, color: '#059669', marginTop: 6, fontWeight: 600 }}>
                                                      → {rec.expected_outcome}
                                                    </div>
                                                  </>
                                                )}
                                              </div>
                                            );
                                          })}
                                          {kw.all_recommendations.length > 3 && (
                                            <div style={{ fontSize: 11, color: '#94a3b8', textAlign: 'center', padding: '4px' }}>
                                              + {kw.all_recommendations.length - 3} more recommendation(s)
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })()}
                                  
                                  <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>

                                    {/* Opportunity breakdown */}
                                    <div style={{ minWidth: 180 }}>
                                      <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Opportunity Breakdown</div>
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                        {[
                                          ['Position', pos.toFixed(1), posColor(pos)],
                                          ['Volume', fmtVol || 'Low', volumeColor(vol.volume)],
                                          ['KD', kd !== null && kd !== undefined ? `${kd}/100` : '—', kdC.text],
                                          ['CPC', vol.cpc > 0 ? `$${vol.cpc.toFixed(2)}` : '—', '#16a34a'],
                                          ['Opp Score', `${score}/100`, scoreC.text],
                                        ].map(([label, value, color]) => (
                                          <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 8px', background: '#fff', borderRadius: 6, border: '1px solid #e2e8f0' }}>
                                            <span style={{ fontSize: 12, color: '#64748b' }}>{label}</span>
                                            <span style={{ fontSize: 13, fontWeight: 700, color }}>{value}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>

                                    {/* Volume sparkline expanded */}
                                    {vol.monthly_searches && vol.monthly_searches.length > 1 && (
                                      <div style={{ minWidth: 200 }}>
                                        <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Search Volume Trend</div>
                                        <svg width="200" height="60">
                                          {(() => {
                                            const vals = vol.monthly_searches.map(m => m.volume || 0);
                                            const max = Math.max(...vals) || 1; const min = Math.min(...vals);
                                            const pts = vals.map((v, i) => {
                                              const x = (i / (vals.length - 1)) * 196 + 2;
                                              const y = 56 - ((v - min) / (max - min || 1)) * 50;
                                              return `${x},${y}`;
                                            }).join(' ');
                                            const color = vals[vals.length-1] >= vals[0] ? '#16a34a' : '#ef4444';
                                            return <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" />;
                                          })()}
                                        </svg>
                                        <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>
                                          {vol.monthly_searches[0]?.year}-{String(vol.monthly_searches[0]?.month).padStart(2,'0')} → {vol.monthly_searches[vol.monthly_searches.length-1]?.year}-{String(vol.monthly_searches[vol.monthly_searches.length-1]?.month).padStart(2,'0')}
                                        </div>
                                      </div>
                                    )}

                                    <div style={{ width: 1, background: '#e2e8f0', alignSelf: 'stretch' }} />

                                    {/* Position trend */}
                                    {isLoadingTrend ? (
                                      <div style={{ color: '#64748b', fontSize: 13 }}>Loading position trend...</div>
                                    ) : trend?.trend_data && trend.trend_data.length > 1 ? (
                                      <div style={{ flex: 1, minWidth: 260 }}>
                                        <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.4px' }}>30-Day Position History</div>
                                        <TrendChart trendData={trend.trend_data} width={340} height={120} />
                                        <div style={{ display: 'flex', gap: 20, marginTop: 8, flexWrap: 'wrap' }}>
                                          {[
                                            ['Best', trend.statistics?.best_position, '#22c55e'],
                                            ['Worst', trend.statistics?.worst_position, '#ef4444'],
                                            ['Avg', trend.statistics?.avg_position, '#667eea'],
                                            ['Trend', trend.statistics?.trend_direction, trend.statistics?.trend_direction === 'IMPROVING' ? '#22c55e' : trend.statistics?.trend_direction === 'DECLINING' ? '#ef4444' : '#f59e0b'],
                                          ].map(([label, val, color]) => (
                                            <div key={label} style={{ textAlign: 'center', minWidth: 60 }}>
                                              <div style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 2 }}>{label}</div>
                                              <div style={{ fontSize: 16, fontWeight: 700, color }}>{val ?? '—'}</div>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    ) : (
                                      <div style={{ color: '#94a3b8', fontSize: 12 }}>Not enough position history yet</div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {Math.ceil(total / perPage) > 1 && (
                  <div className="pagination">
                    <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="btn-secondary">← Previous</button>
                    <span className="page-info">Page {currentPage} of {Math.ceil(total / perPage)} · Showing {sorted.length} of {keywords.length} keywords</span>
                    <button onClick={() => setCurrentPage(p => Math.min(Math.ceil(total / perPage), p + 1))} disabled={currentPage === Math.ceil(total / perPage)} className="btn-secondary">Next →</button>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* SERP Panel */}
      {serpKeyword && (
        <SerpPanel
          keyword={serpKeyword}
          websiteId={websiteId}
          clientDomain={''}
          onClose={() => setSerpKeyword(null)}
        />
      )}
    </div>
  );
}

export default Keywords;
